import isEmpty from 'lodash/isEmpty';
import mergeWith from 'lodash/mergeWith';
import isNil from 'lodash/isNil';
import camelCase from 'lodash/camelCase';

import {
    ELEMENT_LINK_OUT,
    ELEMENT_ADD_EMBED,
    ELEMENT_ALT_ZOOM_LINK_OUT,
    ELEMENT_EMBED,
    ELEMENT_ALT_EMBED,
    ELEMENT_ZOOM_LINK_OUT,
    ELEMENT_MODAL_LOGO,
    ELEMENT_LOGO,
    ELEMENT_FOOTER_CTA,
    ELEMENT_ALT_EMBED_EVENT,
    ELEMENT_ZOOM_LINK_OUT_EVENT,
    ELEMENT_LINK_OUT_EVENT,
} from 'common/constants/virtualEvent';

import {
    allStylesMap,
    logoMap,
    metaMap,
    layoutMap,
    stylesMapKeys,
    metaMapKeys,
    checkForUnknownKeys,
    EMPTY_BORDER_WIDTH,
} from '../helpers';

// Add Embed does not need its own data structure. This seems like an oversight.
// We do not allow Zoom to be in the alt embed anymore.
const DATA_STRUCTURES_TO_BE_DELETED = [ELEMENT_ADD_EMBED, ELEMENT_ALT_ZOOM_LINK_OUT];

// This removes dynamic tags link [link_out]
const dynamicTagRegex = new RegExp(/^\[.*\]$/);

/* eslint-disable no-param-reassign */

/**
 * SENSITIVE CODE AHEAD
 *
 * Basically, it felt annoyingly complex to move all of these functions into a separate file
 * because they are very specific to this use case.
 *
 * We are directly mutating all of the params passed to these functions and then returning nothing.
 * I would normally say this is AWFUL but it honestly is so much cleaner and less spaghetti code.
 *
 */
function convertLinkOut({ descendants, mergedStyles, restProperties, newMeta, newStyles, buttonLabel }) {
    const validatedHref = !dynamicTagRegex.test(restProperties.buttonHref) ? restProperties.buttonHref : '';
    newStyles.backgroundColor = mergedStyles.backgroundColorLinkOut;

    descendants.headline = {
        styles: {
            fontFamily: mergedStyles.headlineFontFamily,
            fontSize: mergedStyles.headlineFontSize,
            fontColor: mergedStyles.headlineColor,
        },
        meta: {
            content: restProperties.headline,
        },
    };
    descendants.button = {
        styles: {
            fontFamily: mergedStyles.buttonFontFamily,
            fontSize: mergedStyles.buttonFontSize,
            fontColor: mergedStyles.buttonColor,
            fontColorHover: mergedStyles.buttonColorHover,
            backgroundColor: mergedStyles.buttonBackgroundColor,
            backgroundColorHover: mergedStyles.buttonBackgroundColorHover,
            borderWidth: { ...EMPTY_BORDER_WIDTH },
        },
        meta: {
            content: buttonLabel,
            ...(validatedHref ? { href: validatedHref } : {}),
        },
    };

    delete newMeta.content; // unused
    delete newMeta.headline; // moved to descendants.headline.content
    delete newMeta.href; // moved to descendants.button.content
    delete newMeta.virtualContentType; // unused

    delete newStyles.backgroundColorHover; // moved to descendants.button.backgroundColorHover
    delete newStyles.fontColor; // moved to descendants
    delete newStyles.fontColorHover; // moved to descendants
    delete newStyles.fontFamily; // moved to descendants
    delete newStyles.fontSize; // moved to descendants
}

function convertAltEmbed({ newStyles, newMeta, mergedStyles }) {
    newStyles.backgroundColor = mergedStyles.backgroundColorEmbed;

    delete newMeta.content; // unused
    delete newMeta.headline; // unused
    delete newMeta.href; // unused
    delete newMeta.zoomPreEventButtonLabel; // unused
    delete newMeta.zoomPreEventHeadline; // unused

    delete newStyles.backgroundColorEmbed; // moved to backgroundColor
    delete newStyles.backgroundColorHover; // unused
    delete newStyles.fontColor; // unused
    delete newStyles.fontColorHover; // unused
    delete newStyles.fontFamily; // unused
    delete newStyles.fontSize; // unused
}

function convertZoomLinkOut({ newMeta, newStyles }) {
    delete newMeta.headline; // unused

    delete newStyles.backgroundColor; // unused
    delete newStyles.backgroundColorHover; // unused
}

function convertEmbed({ descendants, mergedStyles, restProperties, newMeta, newStyles, buttonLabel }) {
    newStyles.backgroundColor = mergedStyles.backgroundColorEmbed;

    descendants.zoomButton = {
        styles: {
            fontFamily: mergedStyles.zoomPreEventButtonFontFamily,
            fontSize: mergedStyles.zoomPreEventButtonFontSize,
            fontColor: mergedStyles.zoomPreEventButtonColor,
            fontColorHover: mergedStyles.zoomPreEventButtonColorHover,
            backgroundColor: mergedStyles.zoomPreEventButtonBackgroundColor,
            backgroundColorHover: mergedStyles.zoomPreEventButtonBackgroundColorHover,
            borderWidth: { ...EMPTY_BORDER_WIDTH },
        },
        meta: {
            content: restProperties.zoomPreEventButtonLabel,
        },
    };
    descendants.zoomHeadline = {
        styles: {
            fontFamily: mergedStyles.zoomPreEventHeadlineFontFamily,
            fontSize: mergedStyles.zoomPreEventHeadlineFontSize,
            fontColor: mergedStyles.zoomPreEventHeadlineColor,
        },
        meta: {
            content: restProperties.zoomPreEventHeadline,
        },
    };

    delete newMeta.content; // unused
    delete newMeta.headline; // unused
    delete newMeta.href; // unused
    delete newMeta.zoomPreEventButtonLabel; // now descendants.zoomButton.meta.content
    delete newMeta.zoomPreEventHeadline; // now descendants.zoomHeadline.meta.content

    delete newStyles.backgroundColorHover; // unused
    delete newStyles.fontColor; // unused
    delete newStyles.fontColorHover; // unused
    delete newStyles.fontFamily; // unused
    delete newStyles.fontSize; // unused
}
/* eslint-enable no-param-reassign */

/**
 * This function converts the legacy VEP During Event data structure
 * to the new Splash CMS Engine data structure
 *
 * The relevant elements that will be transformed are:
 * - body
 * - header
 * - headline * UNIQUE
 * - logo * UNIQUE
 * - virtual_content * UNIQUE
 * - embed * UNIQUE
 * - alt_embed * UNIQUE
 * - add_embed * DELETED
 * - link_out * UNIQUE
 * - zoom_link_out * UNIQUE
 * - alt_zoom_link_out * DELETED
 * - footer
 * - contact
 * - eventPage
 * - footerCTA
 *
 * @param {object} newDataStructure - an accumulator object that will be used to store the transformed data
 * @param {string} elementId - the element ID of the element being transformed
 * @param {object} elementConfig - the element configuration of the element being transformed
 */
export const transformDuringEventStateData = (newDataStructure, [elementId, elementConfig]) => {
    if (DATA_STRUCTURES_TO_BE_DELETED.includes(elementId)) {
        return newDataStructure;
    }

    const descendants = {};
    const { styles, properties, content, overrides = {} } = elementConfig;

    const defaultProperties = Array.isArray(properties) ? {} : properties;
    const defaultStyles = Array.isArray(styles) ? {} : styles;
    const overrideProperties = Array.isArray(overrides?.properties) ? {} : overrides?.properties;
    const overrideStyles = Array.isArray(overrides?.styles) ? {} : overrides?.styles;

    const mergedProperties = mergeWith({}, defaultProperties, overrideProperties || {}, (a, b) => (isNil(b) ? a : b));
    const mergedStyles = mergeWith({}, defaultStyles, overrideStyles || {}, (a, b) => (isNil(b) ? a : b));

    const { logoType, label, buttonLabel, image, layoutType, type, ...restProperties } = mergedProperties;
    const metaContent = label || buttonLabel || content;

    const newStyles = Object.entries(mergedStyles).reduce((acc, [key, value]) => {
        const engineKey = stylesMapKeys.includes(key) ? allStylesMap[key] : key;

        if (!isNil(value)) {
            acc[engineKey] = value;
        }

        return acc;
    }, {});

    const newMeta = Object.entries(restProperties).reduce((acc, [key, value]) => {
        const engineKey = metaMapKeys.includes(key) ? metaMap[key] : key;

        if (!isNil(value)) {
            acc[engineKey] = value;
        }

        return acc;
    }, {});

    if (logoType) {
        newMeta.logoType = logoMap[logoType];
    } else if (elementId === ELEMENT_MODAL_LOGO || elementId === ELEMENT_LOGO) {
        newMeta.logoType = logoMap.customLogo;
    }

    if (image) {
        newStyles.backgroundImageUrl = image;
    }

    if (metaContent && !dynamicTagRegex.test(metaContent)) {
        newMeta.content = metaContent;
    }

    if (layoutType) {
        newMeta.layout = layoutMap[layoutType];
    }

    if (type) {
        newMeta.virtualContentType = type;
    }

    if (elementId === ELEMENT_FOOTER_CTA) {
        newStyles.borderWidth = { ...EMPTY_BORDER_WIDTH };
    }

    if (elementId === ELEMENT_LINK_OUT || elementId === ELEMENT_LINK_OUT_EVENT) {
        convertLinkOut({ descendants, mergedStyles, restProperties, newMeta, newStyles, buttonLabel });
    }

    if (elementId === ELEMENT_ALT_EMBED || elementId === ELEMENT_ALT_EMBED_EVENT) {
        convertAltEmbed({ newStyles, newMeta, mergedStyles });
    }

    if (elementId === ELEMENT_ZOOM_LINK_OUT || elementId === ELEMENT_ZOOM_LINK_OUT_EVENT) {
        convertZoomLinkOut({ newMeta, newStyles });
    }

    if (elementId === ELEMENT_EMBED) {
        convertEmbed({ descendants, mergedStyles, restProperties, newMeta, newStyles, buttonLabel });
    }

    const camelizedElementID = camelCase(elementId);

    // this object MUST be in the { props } format to match the saved data structure
    // eslint-disable-next-line no-param-reassign
    newDataStructure[camelizedElementID] = {
        props: {
            styles: newStyles,
            meta: newMeta,
            ...(!isEmpty(descendants) ? { descendants } : {}),
        },
    };

    checkForUnknownKeys(mergedProperties, mergedStyles, elementId);

    return newDataStructure;
};
